import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import LoadingApp from '../components-library/loading/LoadingApp';
import { AnimatePresence } from 'framer-motion';
import ConfigManager from '../services/api/ConfigManager';
import { ConfigInterface } from '../services/exports/Interfaces';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import useStorefront from '../hooks/storefront/useStorefront';
import useMenu from '../hooks/menu/useMenu';

interface Props {
  children: ReactElement;
}

export interface ILoadingContext {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  renderLoadingApp: () => ReactElement;
}

export const LoadingContext = React.createContext<ILoadingContext | null>(null);

const LoadingProvider = (props: Props): ReactElement => {
  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { loading: loadingStorefront } = useStorefront();
  const { loadingMenu } = useMenu();

  const mobileAppConfig = window?.mobileAppConfig?.data?.config;
  const [config, setConfig] = useState<ConfigInterface | null>(mobileAppConfig ?? null);
  const [loading, setLoading] = useState(mobileAppConfig ? false : true);
  const isLoading = loading || loadingStorefront || loadingMenu;

  async function requestConfig(): Promise<void> {
    const { response, success } = await ConfigManager.get();

    if (success && response?.data?.data) {
      setConfig(response.data.data);
    }

    company?.id && setTimeout(() => setLoading(false), 1000);
  }

  useEffect(() => {
    if (!mobileAppConfig) {
      requestConfig();
    }
  }, []);

  useEffect(() => {
    company?.id && setTimeout(() => setLoading(false), 1000);
  }, [company?.id]);

  const renderLoadingApp = () => (
    <AnimatePresence>
      {isLoading && <LoadingApp color={config?.primary_color} logo={config?.logo_url} />}
    </AnimatePresence>
  );

  const context = useMemo(
    () => ({
      loading: isLoading,
      setLoading,
      renderLoadingApp,
    }),
    [loading, loadingStorefront, loadingMenu],
  );

  return (
    <LoadingContext.Provider value={context}>
      {children}
      {renderLoadingApp()}
    </LoadingContext.Provider>
  );
};

export default LoadingProvider;
